import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { PublicKey, Transaction } from '@solana/web3.js';
import { createBurnCheckedInstruction, getAssociatedTokenAddress, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';
import bs58 from 'bs58';
import { apiFetch, getApiHost } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';
import { redirectToPhantomIfMobile } from '../utils/phantomMobile';
import {
  persistentPhantomWalletMatches,
  startPhantomSignTransaction,
  consumePhantomTxResult,
} from '../utils/phantomDeeplink';
import {
  hasLiveWCSessionSync,
  signTransactionViaWC,
} from '../utils/walletConnectMobile';

// Note: Balance is fetched via API (server-side RPC) to avoid exposing RPC keys.
// Burn transactions still go through the wallet adapter + public RPC for signing.

const DNET_MINT = new PublicKey('93VXFhKsGXsraivwdwAwoVXHKd4U1TFL64oQYpVjpump');
const DNET_DECIMALS = 6;

interface BurnPricing {
  dnetPriceUsd: number;
  usdPerCredit: number;
  tokensPerCredit: number;
  mint: string;
  bundles: {
    credits: number;
    discount: number;
    label: string;
    totalTokens: number;
    totalUsd: number;
  }[];
}

interface BurnCreditsProps {
  onCreditsAdded: () => void;
}

const BurnCredits: React.FC<BurnCreditsProps> = ({ onCreditsAdded }) => {
  const { publicKey, signTransaction } = useWallet();
  const { setVisible: setWalletModalVisible } = useWalletModal();
  const { user } = useAuth();

  // Use auth context wallet for display, wallet adapter for signing
  const authWalletAddress = user?.walletAddress ?? null;
  const displayWalletAddress = publicKey?.toBase58() ?? authWalletAddress;

  const [pricing, setPricing] = useState<BurnPricing | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  const [selectedBundle, setSelectedBundle] = useState<number>(0);
  const [burning, setBurning] = useState(false);
  const [burnStatus, setBurnStatus] = useState<string | null>(null);
  const [burnError, setBurnError] = useState<string | null>(null);
  const [burnSuccess, setBurnSuccess] = useState<{ credits: number; txSignature: string } | null>(null);

  const [dnetBalance, setDnetBalance] = useState<number | null>(null);
  const [dnetHeld, setDnetHeld] = useState<number | null>(null);

  const fetchPricing = useCallback(async () => {
    try {
      setLoadingPricing(true);
      setPricingError(null);
      const response = await apiFetch(`${getApiHost()}/api/burn/price`);
      if (!response.ok) throw new Error('Failed to fetch pricing');
      const data = await response.json();
      setPricing(data);
    } catch (err) {
      setPricingError(err instanceof Error ? err.message : 'Failed to load pricing');
    } finally {
      setLoadingPricing(false);
    }
  }, []);

  // Stabilize on the string form so re-renders don't retrigger fetches
  const walletAddress = displayWalletAddress;

  const fetchDnetBalance = useCallback(async () => {
    if (!walletAddress) return;
    try {
      const response = await apiFetch(
        `${getApiHost()}/api/burn/dnet-balance/${walletAddress}`
      );
      if (!response.ok) throw new Error('Failed to fetch balance');
      const data = await response.json();
      setDnetBalance(data.balance);
      setDnetHeld(data.held ?? data.balance);
    } catch {
      // Don't overwrite a good balance with 0 on transient errors
      setDnetBalance(prev => prev ?? 0);
    }
  }, [walletAddress]);

  useEffect(() => {
    fetchPricing();
    const interval = setInterval(fetchPricing, 60_000);
    return () => clearInterval(interval);
  }, [fetchPricing]);

  useEffect(() => {
    fetchDnetBalance();
  }, [fetchDnetBalance]);

  /**
   * Send a base64-serialized signed transaction through our API proxy
   * and run the verify-and-grant step. Used by both the wallet-adapter
   * path and the deeplink-callback continuation.
   */
  const submitSignedTx = useCallback(async (serializedBase64: string, expectedCredits: number) => {
    setBurnStatus('Sending transaction...');
    const sendRes = await apiFetch(
      `${getApiHost()}/api/burn/send-transaction`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaction: serializedBase64 }),
      }
    );
    if (!sendRes.ok) {
      const errData = await sendRes.json();
      throw new Error(errData.error || 'Failed to send transaction');
    }
    const { signature } = await sendRes.json();

    setBurnStatus('Verifying burn and granting credits...');
    const token = localStorage.getItem('deepnets_token');
    const verifyResponse = await apiFetch(
      `${getApiHost()}/api/burn/verify`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ txSignature: signature, expectedCredits }),
      }
    );
    if (!verifyResponse.ok) {
      const errData = await verifyResponse.json();
      throw new Error(errData.error || 'Verification failed');
    }
    const result = await verifyResponse.json();
    setBurnSuccess({ credits: result.creditsGranted, txSignature: signature });
    setBurnStatus(null);
    fetchDnetBalance();
    onCreditsAdded();
  }, [fetchDnetBalance, onCreditsAdded]);

  // Pick up Phantom-deeplink-tx continuations on mount. When the user
  // returns from a signTransaction bounce, the URL has
  // `?phantom-tx-pending=1` and the signed tx is in localStorage.
  // Submit it through the same pipeline as the wallet-adapter path.
  const continuationRanRef = useRef(false);
  useEffect(() => {
    if (continuationRanRef.current) return;
    if (!new URLSearchParams(window.location.search).has('phantom-tx-pending')) return;
    continuationRanRef.current = true;

    const tx = consumePhantomTxResult('burn-credits');
    if (!tx) return;
    // Strip the flag so a refresh doesn't re-fire. Only do this once
    // we've actually consumed — leaving it on a no-match means the
    // sibling component still needs to act on it.
    const url = new URL(window.location.href);
    url.searchParams.delete('phantom-tx-pending');
    window.history.replaceState({}, '', url.toString());

    if (tx.error) {
      setBurnError(tx.error.toLowerCase().includes('cancel') ? 'Transaction cancelled' : tx.error);
      setBurning(false);
      return;
    }
    if (!tx.signedTransactionBase58) {
      setBurnError('Phantom returned no signed transaction');
      setBurning(false);
      return;
    }
    const expectedCredits = Number(tx.params.credits) || 0;
    // Phantom's signTransaction returns the tx as base58. Our send API
    // expects base64.
    const txBytes = bs58.decode(tx.signedTransactionBase58);
    const base64 = btoa(String.fromCharCode(...txBytes));
    setBurning(true);
    setBurnError(null);
    setBurnSuccess(null);
    submitSignedTx(base64, expectedCredits).catch(err => {
      setBurnError(err?.message || 'Burn failed');
      setBurnStatus(null);
      setBurning(false);
    }).finally(() => setBurning(false));
  }, [submitSignedTx]);

  const handleBurn = async () => {
    if (!pricing) return;

    const bundle = pricing.bundles[selectedBundle];
    if (!bundle) return;

    trackEvent('buy_credits_clicked', { method: 'burn', bundle: bundle.credits, amountUsd: bundle.totalUsd });

    // Mobile fast path: if our persistent Phantom session is for the
    // SAME wallet the user is currently authed with, build the tx and
    // bounce to Phantom's signTransaction endpoint instead of routing
    // through the wallet-adapter modal (which doesn't work on mobile
    // Safari without extension injection). The wallet-match guard is
    // load-bearing — without it, a user who previously signed in with
    // Phantom but is now signed in with Jupiter (WC) would get sent
    // through Phantom for the burn even though their auth wallet is a
    // different account.
    const usePhantomDeeplink = !publicKey && persistentPhantomWalletMatches(authWalletAddress);
    if (usePhantomDeeplink) {
      try {
        setBurning(true);
        setBurnError(null);
        setBurnStatus('Preparing burn transaction...');
        const burnerPubkey = new PublicKey(authWalletAddress!);
        const tokenAmount = BigInt(bundle.totalTokens) * BigInt(10 ** DNET_DECIMALS);

        const burnableBalance = dnetHeld ?? dnetBalance;
        if (burnableBalance !== null && burnableBalance < bundle.totalTokens) {
          throw new Error(
            `Insufficient unstaked $DNET. Need ${bundle.totalTokens.toLocaleString()} but have ${burnableBalance.toLocaleString()} unstaked`
          );
        }

        const ata = await getAssociatedTokenAddress(DNET_MINT, burnerPubkey, false, TOKEN_2022_PROGRAM_ID);
        const burnIx = createBurnCheckedInstruction(
          ata, DNET_MINT, burnerPubkey, tokenAmount, DNET_DECIMALS, [], TOKEN_2022_PROGRAM_ID,
        );

        const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
        if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
        const { blockhash } = await blockhashRes.json();

        const transaction = new Transaction().add(burnIx);
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = burnerPubkey;

        // Phantom's signTransaction wants the tx as base58.
        const serializedBytes = transaction.serialize({ requireAllSignatures: false });
        const transactionBase58 = bs58.encode(serializedBytes);

        startPhantomSignTransaction({
          transactionBase58,
          operation: 'burn-credits',
          params: { credits: bundle.credits },
          returnUrl: window.location.pathname + window.location.search,
        });
        // Page navigates away from here; control resumes via the
        // useEffect continuation when Phantom redirects back.
        return;
      } catch (err: any) {
        setBurnError(err?.message || 'Burn failed');
        setBurnStatus(null);
        setBurning(false);
        return;
      }
    }

    // Mobile fast path #2: Jupiter (or any other WC) session live for
    // the same auth wallet → sign over the WC relay. Unlike Phantom's
    // deeplink, this stays on the same page (no continuation), so we
    // can just await the request like we would on desktop.
    const useWalletConnect = !publicKey && !!authWalletAddress && hasLiveWCSessionSync(authWalletAddress);
    if (useWalletConnect) {
      setBurning(true);
      setBurnError(null);
      setBurnSuccess(null);
      setBurnStatus('Preparing burn transaction...');
      try {
        const burnerPubkey = new PublicKey(authWalletAddress!);
        const tokenAmount = BigInt(bundle.totalTokens) * BigInt(10 ** DNET_DECIMALS);

        const burnableBalance = dnetHeld ?? dnetBalance;
        if (burnableBalance !== null && burnableBalance < bundle.totalTokens) {
          throw new Error(
            `Insufficient unstaked $DNET. Need ${bundle.totalTokens.toLocaleString()} but have ${burnableBalance.toLocaleString()} unstaked`
          );
        }

        const ata = await getAssociatedTokenAddress(DNET_MINT, burnerPubkey, false, TOKEN_2022_PROGRAM_ID);
        const burnIx = createBurnCheckedInstruction(
          ata, DNET_MINT, burnerPubkey, tokenAmount, DNET_DECIMALS, [], TOKEN_2022_PROGRAM_ID,
        );

        const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
        if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
        const { blockhash } = await blockhashRes.json();

        const transaction = new Transaction().add(burnIx);
        transaction.recentBlockhash = blockhash;
        transaction.feePayer = burnerPubkey;

        const transactionBase58 = bs58.encode(transaction.serialize({ requireAllSignatures: false }));
        setBurnStatus('Approve in your wallet…');
        const signedBytes = await signTransactionViaWC(transactionBase58);
        const base64 = btoa(String.fromCharCode(...signedBytes));
        await submitSignedTx(base64, bundle.credits);
      } catch (err: any) {
        const msg = err?.message || 'Burn failed';
        setBurnError(msg.toLowerCase().includes('reject') || msg.toLowerCase().includes('cancel') ? 'Transaction cancelled' : msg);
        setBurnStatus(null);
      } finally {
        setBurning(false);
      }
      return;
    }

    // Desktop / extension wallet path.
    if (!publicKey || !signTransaction) {
      // No matching mobile sign path. The auth wallet exists but we
      // can't sign with it from this device — typical when the user
      // signed in with a wallet we don't have a live session for
      // (e.g. signed in via email-link or a no-longer-paired WC).
      if (authWalletAddress) {
        setBurnError(
          'No active wallet session for signing. Sign out and sign in again with the wallet you want to burn from.'
        );
        return;
      }
      if (redirectToPhantomIfMobile()) return;
      setWalletModalVisible(true);
      return;
    }

    setBurning(true);
    setBurnError(null);
    setBurnSuccess(null);
    setBurnStatus('Preparing burn transaction...');

    try {
      const tokenAmount = BigInt(bundle.totalTokens) * BigInt(10 ** DNET_DECIMALS);
      const burnableBalance = dnetHeld ?? dnetBalance;
      if (burnableBalance !== null && burnableBalance < bundle.totalTokens) {
        throw new Error(
          `Insufficient unstaked $DNET. Need ${bundle.totalTokens.toLocaleString()} but have ${burnableBalance.toLocaleString()} unstaked`
        );
      }

      const ata = await getAssociatedTokenAddress(DNET_MINT, publicKey, false, TOKEN_2022_PROGRAM_ID);
      const burnIx = createBurnCheckedInstruction(
        ata, DNET_MINT, publicKey, tokenAmount, DNET_DECIMALS, [], TOKEN_2022_PROGRAM_ID,
      );

      setBurnStatus('Preparing transaction...');
      const blockhashRes = await apiFetch(`${getApiHost()}/api/burn/blockhash`);
      if (!blockhashRes.ok) throw new Error('Could not fetch blockhash');
      const { blockhash } = await blockhashRes.json();

      const transaction = new Transaction().add(burnIx);
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      setBurnStatus('Approve the burn in your wallet...');
      const signed = await signTransaction!(transaction);

      const serialized = signed.serialize().toString('base64');
      await submitSignedTx(serialized, bundle.credits);
    } catch (err: any) {
      console.error('Burn error:', err);
      if (err.message?.includes('User rejected') || err.message?.includes('rejected the request')) {
        setBurnError('Transaction cancelled');
      } else {
        setBurnError(err.message || 'Burn failed');
      }
      setBurnStatus(null);
    } finally {
      setBurning(false);
    }
  };

  if (loadingPricing) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin w-6 h-6 border-2 border-crypto-accent border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-gray-400 text-sm">Loading pricing...</p>
      </div>
    );
  }

  if (pricingError) {
    return (
      <div className="text-center py-4">
        <p className="text-red-400 text-sm mb-2">Could not load pricing</p>
        <button
          onClick={fetchPricing}
          className="text-crypto-accent hover:text-crypto-accent/80 text-sm"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!pricing) return null;

  const currentBundle = pricing.bundles[selectedBundle];

  return (
    <div className="space-y-4">
      {/* Bundle selector */}
      <div className="space-y-2">
        {pricing.bundles.map((bundle, index) => (
          <button
            key={bundle.credits}
            onClick={() => setSelectedBundle(index)}
            className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ${
              selectedBundle === index
                ? 'border-crypto-accent bg-crypto-accent/10'
                : 'border-gray-700 bg-crypto-dark hover:border-gray-600'
            }`}
          >
            <div>
              <span className="text-white font-medium">{bundle.label}</span>
              {bundle.discount > 0 && (
                <span className="ml-2 text-xs text-crypto-accent">Save {bundle.discount * 100}%</span>
              )}
            </div>
            <div className="text-right">
              <div className="text-white font-mono text-sm">
                {bundle.totalTokens.toLocaleString()} DNET
              </div>
              <div className="text-gray-500 text-xs">~${bundle.totalUsd}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Burn button */}
      <button
        onClick={handleBurn}
        disabled={burning || !displayWalletAddress}
        className="w-full bg-orange-600 hover:bg-orange-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
      >
        {burning ? (
          <>
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
            <span>{burnStatus || 'Processing...'}</span>
          </>
        ) : (
          <>
            <span>🔥</span>
            <span>
              Burn {currentBundle?.totalTokens.toLocaleString()} DNET for {currentBundle?.credits} Credit{currentBundle?.credits !== 1 ? 's' : ''}
            </span>
          </>
        )}
      </button>

      {!displayWalletAddress && (
        <p className="text-gray-500 text-xs text-center">Connect your wallet to burn $DNET</p>
      )}

      {/* Status messages */}
      {burnError && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{burnError}</p>
        </div>
      )}

      {burnSuccess && (
        <div className="p-3 bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg">
          <p className="text-crypto-accent text-sm font-medium">
            🔥 Burned! +{burnSuccess.credits} credit{burnSuccess.credits !== 1 ? 's' : ''} added
          </p>
          <a
            href={`https://solscan.io/tx/${burnSuccess.txSignature}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white text-xs mt-1 block transition-colors"
          >
            View transaction →
          </a>
        </div>
      )}

      {/* Fine print */}
      <p className="text-gray-600 text-xs text-center">
        Burned tokens are permanently removed from circulation.
        Price updates every 60 seconds.
      </p>
    </div>
  );
};

export default BurnCredits;
